import {BlockedIcon} from '@primer/octicons-react'
import {graphql} from 'react-relay'
import {useFragment} from 'react-relay/hooks'

import {LABELS} from '../constants/labels'
import {getGroupCreatedAt} from '../utils/get-group-created-at'
import {createIssueEventExternalUrl} from '../utils/urls'
import type {BlockingAddedEvent$key} from './__generated__/BlockingAddedEvent.graphql'
import type {BaseDependencyEventProps} from './BlockedByAddedEvent'
import styles from './DependencyEvent.module.css'
import type {IssueLinkFragment} from './IssueLink'
import {IssueLink} from './IssueLink'
import {Ago} from './row/Ago'
import {TimelineRow} from './row/TimelineRow'

type EventItem = BlockingAddedEvent$key & {createdAt?: string}

type BlockingAddedEventProps = BaseDependencyEventProps & {
  queryRef: EventItem
  rollupGroup?: Record<'BlockingAddedEvent', EventItem[]>
}

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
export const BlockingAddedEventFragment = graphql`
  fragment BlockingAddedEvent on BlockingAddedEvent {
    databaseId
    actor {
      avatarUrl(size: 64)
      login
      profileResourcePath
      __typename
      ... on Bot {
        isCopilot
        isAgent
        displayName
      }
      ... on User {
        name
      }
      ... on EnterpriseUserAccount {
        name
      }
      ... on Mannequin {
        name
      }
    }
    createdAt
    blockedIssue {
      __typename
      id
      titleHTML
      url
      number
      stateReason
      repository {
        id
        name
        isPrivate
        owner {
          login
        }
      }
    }
  }
`

export function BlockingAddedEvent({
  queryRef,
  issueUrl,
  onLinkClick,
  highlightedEventId,
  refAttribute,
  rollupGroup,
  repositoryId,
  ownerLogin,
}: BlockingAddedEventProps) {
  const {actor, createdAt, blockedIssue, databaseId} = useFragment(BlockingAddedEventFragment, queryRef)

  if (!blockedIssue) {
    return null
  }

  const highlighted = String(databaseId) === highlightedEventId
  const rolledUpGroup = rollupGroup?.BlockingAddedEvent || []
  const itemsToRender = rolledUpGroup.length === 0 ? [queryRef] : rolledUpGroup
  const eventCreatedAt = getGroupCreatedAt(queryRef.createdAt, rolledUpGroup)

  return (
    <TimelineRow
      highlighted={highlighted}
      refAttribute={refAttribute}
      actor={actor}
      createdAt={createdAt}
      showAgoTimestamp={false}
      deepLinkUrl={createIssueEventExternalUrl(issueUrl, databaseId)}
      onLinkClick={onLinkClick}
      leadingIcon={BlockedIcon}
    >
      <TimelineRow.Main>
        <span>
          {itemsToRender.length === 1
            ? LABELS.timeline.blockingAdded.single
            : LABELS.timeline.blockingAdded.multiple(itemsToRender.length)}
        </span>{' '}
        {eventCreatedAt ? (
          <Ago timestamp={new Date(eventCreatedAt)} linkUrl={createIssueEventExternalUrl(issueUrl, databaseId)} />
        ) : null}
      </TimelineRow.Main>
      <TimelineRow.Secondary>
        <ul className={styles.issueDependenciesList}>
          {itemsToRender.map((item, index) => (
            <BlockingAddedEventItem
              // eslint-disable-next-line @eslint-react/no-array-index-key
              key={`${databaseId}_${index}`}
              event={item}
              targetRepositoryId={repositoryId}
              targetOwnerLogin={ownerLogin}
            />
          ))}
        </ul>
      </TimelineRow.Secondary>
    </TimelineRow>
  )
}

function BlockingAddedEventItem({
  event,
  targetRepositoryId,
  targetOwnerLogin,
}: {
  event: BlockingAddedEvent$key
  targetRepositoryId: string
  targetOwnerLogin: string
}) {
  const {blockedIssue} = useFragment(BlockingAddedEventFragment, event)

  if (!blockedIssue) {
    return null
  }

  return (
    <li className={styles.issueDependencyItem}>
      {/* RELAY-CLEANUP(BlockingAddedEvent): boundary cast; remove when BlockingAddedEvent leaves Relay. */}
      <IssueLink
        data={blockedIssue as IssueLinkFragment}
        targetRepositoryId={targetRepositoryId}
        targetOwnerLogin={targetOwnerLogin}
      />
    </li>
  )
}
